import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { publicClient } from '@/api/publicClient';
import type { components } from '@/api/generated/schema';

export type PublicAppointmentEmails = components['schemas']['PublicAppointmentEmailsResponse'];

/**
 * A 404 from the preference link: malformed, unknown, a shop no longer active,
 * a recipient since deleted. The API makes every one the same answer on
 * purpose, so the page does too — one "no longer valid" page that names no shop.
 */
export class PublicAppointmentEmailsGoneError extends Error {
  constructor() {
    super('This link is no longer valid');
    this.name = 'PublicAppointmentEmailsGoneError';
  }
}

const queryKey = (token: string | undefined) => ['public-appointment-emails', token] as const;

/**
 * The recipient's appointment-email preference, read through their link
 * (M2-21) with no sign-in — `publicClient`, never `apiClient`, for M2-07's
 * reasons (no bearer token; no 401 can send a customer to the office's
 * sign-in page).
 *
 * ⚠️ A GET changes nothing, on the API or here: mail scanners open every
 * link in an email. Not refetched on focus, and no retry on a 404 (an answer).
 */
export function usePublicAppointmentEmails(token: string | undefined) {
  return useQuery({
    enabled: token !== undefined,
    queryKey: queryKey(token),
    queryFn: async () => {
      const { data, response } = await publicClient.GET('/api/v1/public/appointment-emails/{token}', {
        params: { path: { token: token! } },
      });
      if (response.status === 404) throw new PublicAppointmentEmailsGoneError();
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/public/appointment-emails/{token} failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Turns the recipient's appointment emails off or back on — an explicit POST
 * from a button, never on load. The answer is the page's new state, so it
 * replaces the cached read rather than refetching it.
 */
export function useSetPublicAppointmentEmails(token: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data, response } = await publicClient.POST('/api/v1/public/appointment-emails/{token}', {
        params: { path: { token: token! } },
        body: { enabled },
      });
      if (response.status === 404) throw new PublicAppointmentEmailsGoneError();
      if (!response.ok || !data) {
        throw new Error(`POST /api/v1/public/appointment-emails/{token} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: (data) => queryClient.setQueryData(queryKey(token), data),
  });
}
